<template>
  <div class="instant-campaign-metrics">
    <!-- Header -->
    <div class="instant-campaign-metrics__header">
      <div class="d-flex align-items-center">
        <b-icon
          icon="arrow-left-square-fill"
          class="back-arrow h3 mb-0 cursor-pointer"
          @click="$router.go(-1)"
        ></b-icon>
        <div class="ml-3">
          <h1 class="page-title">
            Métricas: "{{ campaignData ? campaignData.nombre : '' }}"
          </h1>
          <p class="page-subtitle">Inicio / Campañas Instantáneas / Métricas</p>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando métricas...</p>
    </div>

    <!-- Error -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
      {{ error }}
    </b-alert>

    <!-- Contenido -->
    <div v-if="!loading && metricas" class="metrics-content">
      <!-- Cards de métricas -->
      <div class="metrics-cards">
        <div class="metric-card">
          <div class="metric-label">Total Enviados</div>
          <div class="metric-value">{{ metricas.total_enviados | formatNumber }}</div>
          <div class="metric-percentage"><span>100%</span></div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Entregados</div>
          <div class="metric-value">{{ metricas.total_entregados | formatNumber }}</div>
          <div class="metric-percentage">
            <span>{{ metricas.tasa_entrega }}%</span> Tasa de Entrega
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Abiertos</div>
          <div class="metric-value">{{ metricas.total_abiertos | formatNumber }}</div>
          <div class="metric-percentage">
            <span>{{ metricas.tasa_apertura }}%</span> Tasa de Apertura
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Clics</div>
          <div class="metric-value">{{ metricas.total_clics | formatNumber }}</div>
          <div class="metric-percentage">
            <span>{{ metricas.tasa_clics }}%</span> Tasa de Clics
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Rebotados</div>
          <div class="metric-value">{{ metricas.total_rebotados | formatNumber }}</div>
          <div class="metric-percentage">
            <span>{{ metricas.tasa_rebote }}%</span> Tasa de Rebote
          </div>
        </div>
      </div>

      <!-- Gráficos -->
      <div class="charts-row">
        <div class="chart-card">
          <h3 class="chart-title">Distribución de Entrega</h3>
          <div class="chart-container-dona">
            <canvas ref="donutChart"></canvas>
          </div>
        </div>

        <div class="chart-card chart-card-large">
          <h3 class="chart-title">Evolución (Últimos 30 Días)</h3>
          <div class="chart-container-line">
            <canvas ref="lineChart"></canvas>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import instantCampaignService from '@/services/instantCampaignService';

export default {
  name: 'InstantCampaignMetrics',
  filters: {
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toLocaleString('es-CL');
    },
  },
  data() {
    return {
      campaignData: null,
      metricas: null,
      loading: false,
      error: null,
      donutChart: null,
      lineChart: null,
    };
  },
  computed: {
    campaignId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.cargarDatos();
  },
  beforeDestroy() {
    if (this.donutChart) this.donutChart.destroy();
    if (this.lineChart) this.lineChart.destroy();
  },
  methods: {
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      try {
        const [campana, metricasData] = await Promise.all([
          instantCampaignService.obtenerCampana(this.campaignId),
          instantCampaignService.obtenerMetricas(this.campaignId),
        ]);
        this.campaignData = campana;
        this.metricas = metricasData;
        this.$nextTick(() => {
          this.renderDonutChart();
          this.renderLineChart();
        });
      } catch {
        this.error = 'Error al cargar los datos de la campaña.';
      } finally {
        this.loading = false;
      }
    },

    renderDonutChart() {
      if (!this.$refs.donutChart || !this.metricas) return;
      if (this.donutChart) this.donutChart.destroy();
      const ctx = this.$refs.donutChart.getContext('2d');
      this.donutChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Entregados', 'Rebotados'],
          datasets: [{
            data: [this.metricas.tasa_entrega || 0, this.metricas.tasa_rebote || 0],
            backgroundColor: ['#34C38F', '#F46A6A'],
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '50%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                padding: 15,
                font: { size: 12 },
                generateLabels: (chart) => {
                  const { data } = chart;
                  return data.labels.map((label, i) => ({
                    text: `${label} ${data.datasets[0].data[i]}%`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i,
                  }));
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.label}: ${context.parsed}%`,
              },
            },
          },
        },
      });
    },

    renderLineChart() {
      if (!this.$refs.lineChart || !this.metricas || !this.metricas.evolucion_temporal) return;
      if (this.lineChart) this.lineChart.destroy();
      const ctx = this.$refs.lineChart.getContext('2d');
      const evolucion = this.metricas.evolucion_temporal || [];
      this.lineChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: evolucion.map((item) => item.fecha),
          datasets: [
            {
              label: 'Enviados',
              data: evolucion.map((item) => item.enviados),
              borderColor: '#34C38F',
              backgroundColor: '#34C38F',
              borderWidth: 2,
              tension: 0,
              fill: false,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#34C38F',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
            },
            {
              label: 'Abiertos',
              data: evolucion.map((item) => item.abiertos),
              borderColor: '#F1B44C',
              backgroundColor: '#F1B44C',
              borderWidth: 2,
              tension: 0,
              fill: false,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#F1B44C',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'top', align: 'end' },
          },
          scales: {
            y: { beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.instant-campaign-metrics {
  padding: 24px;
  background: #F5F6FA;
  min-height: 100vh;
}

.instant-campaign-metrics__header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #23201C;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 13px;
  color: #525252;
  margin: 0;
}

.back-arrow {
  color: #FECE2F;
  cursor: pointer;
}

.back-arrow:hover {
  color: #23201C;
}

/* Metric cards */
.metrics-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.metric-label {
  font-size: 13px;
  color: #525252;
  margin-bottom: 8px;
  font-weight: 500;
}

.metric-value {
  font-size: 28px;
  font-weight: 700;
  color: #23201C;
  line-height: 1.1;
  margin-bottom: 6px;
}

.metric-percentage {
  font-size: 13px;
  color: #525252;
}

.metric-percentage span {
  color: #1D4ED8;
  font-weight: 700;
  margin-right: 4px;
}

/* Charts */
.charts-row {
  display: flex;
  gap: 16px;
}

.chart-card {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  flex: 1;
}

.chart-card-large {
  flex: 2;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #23201C;
  margin: 0 0 16px 0;
}

.chart-container-dona {
  height: 220px;
  position: relative;
}

.chart-container-line {
  height: 220px;
  position: relative;
}

@media (max-width: 991px) {
  .metrics-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-row {
    flex-direction: column;
  }
}

@media (max-width: 575px) {
  .metrics-cards {
    grid-template-columns: 1fr;
  }
}
</style>
